/** Axios API client for Control Plane backend. */
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

// Use relative URLs so requests go through the proxy (same origin)
// This ensures cookies are sent with requests
const API_BASE_URL = "";

// CSRF token cache
let cachedCsrfToken: string | null = null;

/** Get CSRF token from cookie. */
export const getCsrfTokenFromCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
};

/** Fetch fresh CSRF token from backend. */
async function fetchCsrfToken(): Promise<string> {
  const response = await axios.get(`${API_BASE_URL}/api/v1/auth/csrf-token`, {
    withCredentials: true,
  });
  cachedCsrfToken = response.data.csrf_token;
  return cachedCsrfToken || "";
}

/** Get CSRF token (from cache or fetch new one). */
export async function getCsrfToken(): Promise<string> {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }
  // Check cookie first
  const cookieToken = getCsrfTokenFromCookie();
  if (cookieToken) {
    cachedCsrfToken = cookieToken;
    return cookieToken;
  }
  // Fetch new token
  return fetchCsrfToken();
}

/** Get JWT token from localStorage (deprecated - kept for backward compatibility). */
export const getToken = (): string | null => {
  return localStorage.getItem("access_token");
};

/** Get reseller JWT token from localStorage (deprecated - kept for backward compatibility). */
export const getResellerToken = (): string | null => {
  return localStorage.getItem("reseller_token");
};

/**
 * Wave 1: fetch wrapper for reseller-portal pages that call the API with the
 * reseller token instead of the shared axios client. Attaches Authorization
 * and the X-CSRF-Token header (required by backend CSRFMiddleware on all
 * state-changing requests — its absence previously caused 403s on transfers).
 */
export async function resellerFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(options.headers || {});
  const token = localStorage.getItem("reseller_token");
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const method = (options.method || "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    try {
      const csrf = await getCsrfToken();
      if (csrf) {
        headers.set("X-CSRF-Token", csrf);
      }
    } catch {
      // CSRF fetch failure will surface as a 403 from the backend
    }
  }
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(url, { ...options, headers });
}

/** Remove JWT token from localStorage (logout). */
export const clearToken = (): void => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("isAuthenticated");
  // Also clear CSRF token
  cachedCsrfToken = null;
};

/** Store JWT token in localStorage (deprecated - tokens now in httpOnly cookies). */
export const setToken = (token: string): void => {
  // No longer storing in localStorage - token is in httpOnly cookie
  // Keep function for backward compatibility but don't store
  console.warn("setToken() is deprecated - tokens are now stored in httpOnly cookies");
};

/** Remove reseller JWT token from localStorage (logout). */
export const clearResellerToken = (): void => {
  localStorage.removeItem("reseller_token");
  localStorage.removeItem("reseller_user_id");
  localStorage.removeItem("reseller_username");
  localStorage.removeItem("reseller_role");
  localStorage.removeItem("reseller_id");
  // Also clear CSRF token
  cachedCsrfToken = null;
};

/** Store reseller JWT token in localStorage (deprecated - tokens now in httpOnly cookies). */
export const setResellerToken = (token: string): void => {
  // No longer storing in localStorage - token is in httpOnly cookie
  // Keep function for backward compatibility but don't store
  console.warn("setResellerToken() is deprecated - tokens are now stored in httpOnly cookies");
};

/** Get appropriate token based on current route (deprecated - tokens in cookies). */
export const getAppropriateToken = (): string | null => {
  // Tokens are now in httpOnly cookies, not accessible via JS
  // Return null to indicate cookie-based auth
  return null;
};

/** Create axios instance with base configuration. */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,  // Include cookies in all requests
});

/** Request interceptor to add auth token and CSRF token. */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add Authorization header with token from localStorage (fallback if cookie doesn't work)
    // Wave 1: fall back to the reseller token so the reseller portal can use the
    // shared API clients (e.g. BalanceTransferDialog via resellersApi) when no
    // admin session exists.
    const token =
      localStorage.getItem("access_token") || localStorage.getItem("reseller_token");
    if (token && config.headers) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing requests
    if (["post", "put", "delete", "patch"].includes(config.method?.toLowerCase() || "")) {
      try {
        const csrfToken = await getCsrfToken();
        if (config.headers) {
          config.headers["X-CSRF-Token"] = csrfToken;
        }
      } catch (error) {
        console.error("Failed to get CSRF token:", error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/** Response interceptor to handle 401 + forced-password-rotation (A1) 403s. */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // A1 (GAP V2): forced credential rotation — backend returns 403 with this
    // exact detail on every endpoint except the auth allowlist while the mcp
    // claim is set. Route to the change-password page (self, reseller portal,
    // and customer portal share it).
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
    if (
      error.response?.status === 403 &&
      detail === "Password change required" &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/change-password"
    ) {
      window.location.href = "/change-password";
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Clear any stale tokens
      clearToken();
      clearResellerToken();

      if (typeof window !== "undefined") {
        // Don't redirect if already on a login page
        const onLogin =
          window.location.pathname === "/login" ||
          window.location.pathname === "/reseller/login" ||
          window.location.pathname === "/customer/login";

        if (!onLogin) {
          // Flag the session expiry so the login page can explain why
          // the user was bounced (instead of a silent hard redirect).
          const isResellerRoute = window.location.pathname.startsWith("/reseller/") ||
            window.location.pathname.startsWith("/customer/");

          const target = isResellerRoute ? "/reseller/login" : "/login";
          window.location.href = `${target}?expired=1`;
        }
      }
    }
    return Promise.reject(error);
  }
);

/** Authentication API endpoints. */
export const authApi = {
  /** Login with username/password (token set in httpOnly cookie). */
  login: (username: string, password: string) =>
    apiClient.post("/api/v1/auth/login", { username, password }),

  /** Validate the current session (cookie or token) — UX review B2. */
  me: () => apiClient.get("/api/v1/auth/me"),

  /** Logout and clear httpOnly cookie. */
  logout: () =>
    apiClient.post("/api/v1/auth/logout"),

  /** Reseller login (token set in httpOnly cookie). */
  resellerLogin: (username: string, password: string) =>
    apiClient.post("/api/v1/auth/reseller/login", { username, password }),

  /** Reseller logout and clear httpOnly cookie. */
  resellerLogout: () =>
    apiClient.post("/api/v1/auth/reseller/logout"),

  /** Preview an invitation before setting a password (public, token-bearer). */
  resellerInvitePreview: (token: string) =>
    apiClient.get(`/api/v1/auth/reseller/invitations/${encodeURIComponent(token)}`),

  /** Accept an invitation — the reseller sets their own password. */
  resellerAcceptInvite: (token: string, password: string) =>
    apiClient.post("/api/v1/auth/reseller/accept-invite", { token, password }),
};

/** Servers API endpoints. */
export const serversApi = {
  /** Get list of all servers. */
  list: () => apiClient.get("/api/v1/servers/"),

  /** Create a new server (returns join_token). */
  create: (data: { hostname: string; server_type: string }) =>
    apiClient.post("/api/v1/servers/", data),

  /** Get details of a specific server. */
  get: (id: string) => apiClient.get(`/api/v1/servers/${id}`),

  /** Delete/remove a server (sets status to draining). */
  remove: (id: string) => apiClient.delete(`/api/v1/servers/${id}`),

  /** Get join script for a server. */
  getJoinScript: (id: string) =>
    apiClient.get(`/api/v1/servers/${id}/join-script`, {
      responseType: "text",
    }),
};

/** Fleet convergence API endpoints (Remediation Plan Phase 2.4). */
export const fleetApi = {
  /** Trigger explicit convergence (dispatcher + FS gateways + IP auth). */
  reconcile: () => apiClient.post("/api/v1/cluster/reconcile"),

  /** Inspect the desired-state artifact agents converge to. */
  desiredState: () => apiClient.get("/api/v1/cluster/desired-state"),
};

/** Agents API endpoints. */
export const agentsApi = {
  /** Download agent binary. */
  getBinary: () =>
    `${API_BASE_URL}/api/v1/agents/binary`,
};

/** Carriers API endpoints. */
export const carriersApi = {
  /** Get list of all carriers. */
  list: () => apiClient.get("/api/v1/carriers/"),

  /** Create a new carrier. */
  create: (data: {
    name: string;
    hostname: string;
    ip_whitelist: string[];
    status?: string;
    capacity_cps?: number;
    capacity_cc?: number;
    priority?: number;
  }) => apiClient.post("/api/v1/carriers/", data),

  /** Get details of a specific carrier. */
  get: (id: string) => apiClient.get(`/api/v1/carriers/${id}`),

  /** Update a carrier. */
  update: (id: string, data: {
    name?: string;
    hostname?: string;
    ip_whitelist?: string[];
    status?: string;
    capacity_cps?: number;
    capacity_cc?: number;
    priority?: number;
  }) => apiClient.put(`/api/v1/carriers/${id}`, data),

  /** Delete a carrier (soft delete, sets status to disabled). */
  remove: (id: string) => apiClient.delete(`/api/v1/carriers/${id}`),

  /** Enable a carrier. */
  enable: (id: string) => apiClient.post(`/api/v1/carriers/${id}/enable`),

  /** Disable a carrier. */
  disable: (id: string) => apiClient.post(`/api/v1/carriers/${id}/disable`),
};

/** Suppliers API endpoints (new - replaces carriers). */
export const suppliersApi = {
  /** Get list of all suppliers with pagination. */
  list: (params?: { page?: number; size?: number }) =>
    apiClient.get("/api/v1/suppliers/", { params }),

  /** Create a new supplier. */
  create: (data: {
    name: string;
    hostname: string;
    status?: string;
    capacity_cps?: number;
    capacity_cc?: number;
    priority?: number;
    cgr_subject?: string;
    tariff_plan?: string;
  }) => apiClient.post("/api/v1/suppliers/", data),

  /** Get details of a specific supplier. */
  get: (id: string) => apiClient.get(`/api/v1/suppliers/${id}`),

  /** Update a supplier. */
  update: (id: string, data: {
    name?: string;
    hostname?: string;
    status?: string;
    capacity_cps?: number;
    capacity_cc?: number;
    priority?: number;
    cgr_subject?: string;
    tariff_plan?: string;
  }) => apiClient.put(`/api/v1/suppliers/${id}`, data),

  /** Delete a supplier. */
  remove: (id: string) => apiClient.delete(`/api/v1/suppliers/${id}`),

  /** Enable a supplier. */
  enable: (id: string) => apiClient.post(`/api/v1/suppliers/${id}/enable`),

  /** Disable a supplier. */
  disable: (id: string) => apiClient.post(`/api/v1/suppliers/${id}/disable`),

  /** Get gateways for a supplier. */
  listGateways: (supplierId: string) =>
    apiClient.get(`/api/v1/suppliers/${supplierId}/gateways`),

  /** Create a gateway for a supplier. */
  createGateway: (supplierId: string, data: {
    hostname?: string;
    ip_address: string;
    port?: number;
    auth_type?: string;
    username?: string;
    password?: string;
    load_share?: number;
    priority?: number;
    capacity_cps?: number;
    capacity_cc?: number;
    enabled?: boolean;
  }) => apiClient.post(`/api/v1/suppliers/${supplierId}/gateways`, data),

  /** Update a gateway. */
  updateGateway: (supplierId: string, gatewayId: number, data: {
    hostname?: string;
    ip_address?: string;
    port?: number;
    auth_type?: string;
    username?: string;
    password?: string;
    load_share?: number;
    priority?: number;
    capacity_cps?: number;
    capacity_cc?: number;
    enabled?: boolean;
  }) => apiClient.put(`/api/v1/suppliers/${supplierId}/gateways/${gatewayId}`, data),

  /** Delete a gateway. */
  removeGateway: (supplierId: string, gatewayId: number) =>
    apiClient.delete(`/api/v1/suppliers/${supplierId}/gateways/${gatewayId}`),
};

/** Billing API endpoints. */
export const billingApi = {
  /** Get billing summary for all accounts. */
  summary: () => apiClient.get("/api/v1/billing/summary"),

  /** Get daily usage statistics. */
  usage: (params: { start_date: string; end_date: string; carrier_id?: string }) =>
    apiClient.get("/api/v1/billing/usage", { params }),

  /** Get top consumers by cost. */
  topConsumers: (params: { start_date: string; end_date: string; limit?: number }) =>
    apiClient.get("/api/v1/billing/top-consumers", { params }),
};

/** CDRs API endpoints. */
export const cdrsApi = {
  /** Query CDRs with filters, pagination, and server-side sorting (D2). */
  list: (params: {
    start_date: string;
    end_date: string;
    carrier_id?: string;
    status?: string;
    destination?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: string;
  }) => apiClient.get("/api/v1/cdrs", { params }),

  /** Query CDRs with filters and pagination (alias for list). */
  query: (params: {
    start_date: string;
    end_date: string;
    carrier_id?: string;
    status?: string;
    destination?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: string;
  }) => apiClient.get("/api/v1/cdrs", { params }),

  /** Export CDRs as CSV. */
  export: (params: {
    start_date: string;
    end_date: string;
    carrier_id?: string;
    status?: string;
    destination?: string;
  }) => apiClient.get("/api/v1/cdrs/export", {
    params,
    responseType: "blob",
  }),

  /** Bulk export selected CDRs. */
  bulkExport: (cdrIds: string[]) =>
    apiClient.post("/api/v1/cdrs/bulk-export", { cdr_ids: cdrIds }, {
      responseType: "blob",
    }),

  /** Get full detail for a single CDR (incl. raw FS channel variables). */
  detail: (callId: string) =>
    apiClient.get(`/api/v1/cdrs/${encodeURIComponent(callId)}/detail`),

  /** Bulk rerate selected CDRs. */
  bulkRerate: (cdrIds: string[]) =>
    apiClient.post("/api/v1/cdrs/bulk-rerate", { cdr_ids: cdrIds }),

  /** Bulk update CDR payment status. */
  bulkUpdateStatus: (cdrIds: string[], status: "paid" | "unpaid") =>
    apiClient.post("/api/v1/cdrs/bulk-status", { cdr_ids: cdrIds, status }),
};

/** Active calls (live sessions) API endpoints. */
export const activeCallsApi = {
  /** List live calls from CGRates SessionS with filters + summary. */
  list: (params: {
    state?: string;
    account?: string;
    destination?: string;
    supplier?: string;
    sort?: string;
    page?: number;
    page_size?: number;
  }) => apiClient.get("/api/v1/active-calls", { params }),

  /** Force-release a live call via CGRates. */
  terminate: (body: { cgrid: string; tenant?: string | null; account?: string | null }) =>
    apiClient.post("/api/v1/active-calls/terminate", body),
};

/** Live media/transcode + resource monitoring (GET /monitoring/media-status). */
export const monitoringApi = {
  getMediaStatus: () => apiClient.get("/api/v1/monitoring/media-status"),
};

/** Destinations API endpoints (Plan 04A-04). */
export const destinationsApi = {
  /** Get popular destinations with call counts. */
  getPopular: (params: {
    search?: string;
    start_date: string;
    end_date: string;
    limit?: number;
  }) => apiClient.get("/api/v1/destinations/popular", { params }),
};

/** CGRates Accounts API endpoints. */
export const accountsApi = {
  /** Get list of all accounts. */
  list: () => apiClient.get("/api/v1/accounts/"),

  /** Get details of a specific account. */
  get: (id: string) => apiClient.get(`/api/v1/accounts/${id}`),

  /** Create a new account. */
  create: (data: {
    tenant: string;
    account: string;
    account_type: string;
    balance?: number;
    credit_limit?: number | null;
  }) => apiClient.post("/api/v1/accounts/", data),

  /** Update an account. */
  update: (id: string, data: {
    tenant?: string;
    account?: string;
    account_type?: string;
    balance?: number;
    credit_limit?: number | null;
    disabled?: boolean;
  }) => apiClient.patch(`/api/v1/accounts/${id}`, data),

  /** Delete an account. */
  remove: (id: string) => apiClient.delete(`/api/v1/accounts/${id}`),

  /** Add balance to an account. */
  addBalance: (id: string, amount: number, reason?: string) =>
    apiClient.post(`/api/v1/accounts/${id}/balance`, { amount, reason }),
};

/** CGRates Status API endpoints. */
export const cgratesApi = {
  /** Get CGrates system status. */
  getStatus: () => apiClient.get("/api/v1/cgrates/status"),

  /** Reload CGrates configuration. */
  reload: () => apiClient.post("/api/v1/cgrates/reload"),
};

/** CGRates LCR API endpoints. */
export const lcrApi = {
  /** Get list of LCR rules. */
  listRules: (tenant: string = "b1") =>
    apiClient.get(`/api/v1/lcr/rules?tenant=${encodeURIComponent(tenant)}`),

  /** Create LCR rule. */
  createRule: (data: {
    tenant: string;
    subject: string;
    destination: string;
    strategy: string;
    carriers: string[];
  }) => apiClient.post("/api/v1/lcr/rules", data),

  /** Delete LCR rule. */
  deleteRule: (id: string, tenant: string) =>
    apiClient.delete(`/api/v1/lcr/rules/${id}?tenant=${encodeURIComponent(tenant)}`),

  /** Query LCR for destination. */
  query: (tenant: string, subject: string, destination: string) =>
    apiClient.get(`/api/v1/lcr/query?tenant=${encodeURIComponent(tenant)}&subject=${encodeURIComponent(subject)}&destination=${encodeURIComponent(destination)}`),

  /** Get carrier costs for destination with margin breakdown. */
  getCarrierCosts: (destination: string, tenant: string = "b1") =>
    apiClient.get(`/api/v1/lcr/carrier-costs?destination=${encodeURIComponent(destination)}&tenant=${encodeURIComponent(tenant)}`),

  /** Get capacity status for all carriers (Plan 05-04). */
  getCapacityStatus: () => apiClient.get("/api/v1/lcr/capacity-status"),
};

/** Resellers API endpoints. */
export const resellersApi = {
  /** Get list of all resellers with optional filtering. */
  list: (params?: { level?: number; parent_id?: string; skip?: number; limit?: number }) =>
    apiClient.get("/api/v1/resellers/", { params }),

  /** Get details of a specific reseller. */
  get: (id: string) => apiClient.get(`/api/v1/resellers/${id}`),

  /** Create a new reseller account. */
  create: (data: {
    tenant: string;
    account: string;
    parent_id?: string | null;
    account_type: string;
    balance?: number;
    credit_limit?: number | null;
    margin_percent?: number | null;
    margin_floor?: number;
    max_depth?: number;
  }) => apiClient.post("/api/v1/resellers/", data),

  /** Update a reseller. */
  update: (id: string, data: {
    margin_percent?: number | null;
    margin_floor?: number | null;
    max_depth?: number;
    disabled?: boolean;
  }) => apiClient.put(`/api/v1/resellers/${id}`, data),

  /** Delete a reseller (soft delete, sets disabled=true). */
  remove: (id: string) => apiClient.delete(`/api/v1/resellers/${id}`),

  /** Get direct children of a reseller. */
  getChildren: (id: string) => apiClient.get(`/api/v1/resellers/${id}/children`),

  /** Transfer balance between reseller accounts (Plan 04-02). */
  transferBalance: (id: string, data: {
    destination_account_id: string;
    amount: number;
  }) => apiClient.post(`/api/v1/resellers/${id}/transfer-balance`, data),

  /** Get transfer history for a reseller account (Plan 04-02). */
  getTransfers: (id: string, limit?: number) =>
    apiClient.get(`/api/v1/resellers/${id}/transfers`, { params: { limit } }),

  /** List portal users (dashboard logins) for a reseller. */
  listUsers: (id: string) => apiClient.get(`/api/v1/resellers/${id}/users`),

  /** Invite a portal user — the reseller sets their own password via a
   *  single-use emailed link; the admin never handles a password. Returns
   *  invite_url only when SMTP is unconfigured (out-of-band relay). */
  inviteUser: (id: string, data: { email: string; username?: string; ttl_hours?: number }) =>
    apiClient.post(`/api/v1/resellers/${id}/users/invite`, data),

  /** Revoke a portal user's access. */
  removeUser: (id: string, userId: string) =>
    apiClient.delete(`/api/v1/resellers/${id}/users/${userId}`),
};

/** Staff (operator/finance) management API endpoints (RBAC). */
export const staffApi = {
  /** List control-plane staff users. */
  list: () => apiClient.get("/api/v1/staff"),

  /** Invite a staff user — same single-use emailed-token flow as reseller
   *  invites; returns invite_url only when SMTP is unconfigured. */
  invite: (data: {
    email: string;
    username?: string;
    role: "operator" | "finance";
    ttl_hours?: number;
  }) => apiClient.post("/api/v1/staff/invite", data),

  /** Revoke a staff user's access (disable + token_version bump). */
  remove: (userId: string) => apiClient.delete(`/api/v1/staff/${userId}`),
};

/** Business KPIs API endpoints (Plan 04A-03). */
export const businessKpisApi = {
  /** Get business KPIs for specified period and tab. */
  getKPIs: (params: {
    period: string;
    tab: string;
    tenant?: string;
  }) => apiClient.get("/api/v1/business/kpis", { params }),

  /** Get provider quality alerts (providers below thresholds). */
  getProviderAlerts: () => apiClient.get("/api/v1/business/provider-quality-alerts"),
};

/** Traffic analysis API endpoints (/traffic page — live CDR aggregates). */
export const trafficApi = {
  /** Get traffic analysis for a period (today | yesterday | last_7_days | last_30_days | this_month). */
  getAnalysis: (period: string) =>
    apiClient.get("/api/v1/traffic/analysis", { params: { period } }),
};

/** Invoices API endpoints. */
export const invoicesApi = {
  /** List all invoices with pagination. */
  list: (params?: {
    customer_id?: string;
    invoice_status?: string;
    page?: number;
    page_size?: number;
  }) => apiClient.get("/api/v1/invoices/", { params }),

  /** Get invoice details with items. */
  get: (id: string) => apiClient.get(`/api/v1/invoices/${id}`),

  /** Generate invoice for customer and period. */
  generate: (data: {
    customer_id: string;
    period_start: string;
    period_end: string;
  }) => apiClient.post("/api/v1/invoices/generate", data),

  /** Update invoice status or PDF path. */
  update: (id: string, data: {
    status?: string;
    pdf_path?: string;
  }) => apiClient.put(`/api/v1/invoices/${id}`, data),

  /** Delete invoice (only draft status). */
  delete: (id: string) => apiClient.delete(`/api/v1/invoices/${id}`),

  /** Download invoice PDF. */
  downloadPdf: (id: string) =>
    apiClient.get(`/api/v1/invoices/${id}/pdf`, { responseType: "blob" }),

  /** Send invoice email to customer. */
  sendEmail: (id: string) => apiClient.post(`/api/v1/invoices/${id}/send-email`),
};

/** Payments API endpoints. */
export const paymentsApi = {
  /** Create payment record for invoice. */
  create: (data: {
    invoice_id: string;
    amount: number;
    payment_method: string;
    transaction_id?: string;
  }) => apiClient.post("/api/v1/payments/", data),

  /** Get payment details. */
  get: (id: string) => apiClient.get(`/api/v1/payments/${id}`),

  /** List all payments for an invoice. */
  listForInvoice: (invoiceId: string) =>
    apiClient.get(`/api/v1/payments/invoice/${invoiceId}`),

  /** Create Stripe PaymentIntent for invoice. */
  createStripeIntent: (invoiceId: string) =>
    apiClient.post(`/api/v1/payments/stripe/create-intent?invoice_id=${invoiceId}`),
};

/** Audit log API endpoints (backend Wave 2.7 audit trail — UX review L2/L3). */
export const auditApi = {
  /** List audit entries with filters. */
  list: (params?: {
    action?: string;
    actor_type?: string;
    actor_id?: string;
    entity_type?: string;
    entity_id?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }) => apiClient.get("/api/v1/audit", { params }),

  /** Get a single audit entry. */
  get: (id: string) => apiClient.get(`/api/v1/audit/${id}`),
};

/** Export the axios instance for custom requests. */
export default apiClient;
